import React, { useState, useEffect } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { ShelterOccupancyPercentageMetric } from "./charts-metrics/ShelterOccupancyPercentageMetric";
import { ShelterIntakeDayMetric } from "./charts-metrics/ShelterIntakeDayMetric";
import { ShelterExitDayMetric } from "./charts-metrics/ShelterExitDayMetric";
import { ShelterVacancyMetric } from "./charts-metrics/ShelterVacancyMetric";
import { ShelterOccupancyLineChart } from "./charts-metrics/ShelterOccupancyLineChart";
import { InfractionsBarChart } from "./charts-metrics/InfractionsBarChart";
import { ShelterOccupiedMetric } from "./charts-metrics/ShelterOccupiedMetric";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import { fetchReportShelterIntakesExits, fetchReportShelterOccupancy } from "../../services/httpClient";
import DataTable from "./ui/DataTable";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { ShelterExitTypePieChart } from "./charts-metrics/ShelterExitTypePieChart";

interface ShelterTabProps {
  buildPDF: () => void;
  canvas: any;
  setCanvas: (canvas: any) => void;
  iPad: boolean;
  isActive?: boolean;
}

const ShelterTab = ({ buildPDF, canvas, setCanvas, iPad, isActive }: ShelterTabProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [reportRunning, setReportRunning] = useState<boolean>(false);
  const [occupancyData, setOccupancyData] = useState<any[] | null>(null);
  const [intakeData, setIntakeData] = useState<any[] | null>(null);
  const [exitData, setExitData] = useState<any[]>([]);
  const [selectedData, setSelectedData] = useState<any[] | null>(null);
  const [activeCard, setActiveCard] = useState<string>("Intakes");

  const intakeExitHeaders = [
    { label: "Name", key: "name" },
    { label: "Intake At", key: "intake_at" },
    { label: "Exit At", key: "exit_at" },
    { label: "Exit Type", key: "exit_type" },
  ];

  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() - dateInterval);
    const startString = start.toISOString().slice(0, 10);
    const endString = new Date().toISOString().slice(0, 10);
    setStartDate(startString);
    setEndDate(endString);
    getData(startString, endString);
  }, []);

  const getData = (start: string, end: string) => {
    getOccupancyData();
    getIntakeExitData();
    setActiveCard("Intakes");
  };

  const getOccupancyData = () => {
    fetchReportShelterOccupancy()
      .then((res: any) => {
        setOccupancyData(res[0].data);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getIntakeExitData = () => {
    fetchReportShelterIntakesExits()
      .then((res: any) => {
        const data = res[0].data;
        setIntakeData(data);
        const exitFiltered = data.filter((item: any) => item.exit_at);
        let exits = exitFiltered.map((item: any) => ({
          exit_at: item.exit_at,
          exit_note_id: item.exit_note_id,
          exit_type: item.exit_type,
        }));
        setExitData(exits);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas]);

  const downloadReport = async () => {
    setReportRunning(true);
    const reportSection = document.getElementById("shelter-section");
    const reportTitle = document.getElementById("shelter-title");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  const onClickCard = (cardId: string) => {
    setActiveCard((prev) => (prev === cardId ? "" : cardId));
  };

  return (
    <div id="shelter-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="shelter-title" className="mb-3">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">Shelter</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Button className="app-primary-color button" onClick={() => downloadReport()}>
                  <FontAwesomeIcon icon={faDownload} />
                  {" Report"}
                </Button>
              )}
            </Col>
          </Row>
          {occupancyData && (
            <Row className="mb-3 justify-content-center">
              <Col xs={3} className="mb-3">
                <ShelterVacancyMetric data={occupancyData} />
              </Col>
              <Col xs={3} className="mb-3">
                <ShelterOccupiedMetric data={occupancyData} />
              </Col>
              <Col xs={3} className="mb-3">
                <ShelterOccupancyPercentageMetric data={occupancyData} />
              </Col>
            </Row>
          )}
          <ReportTimeInterval
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateInterval={dateInterval}
            setDateInterval={setDateInterval}
            intervalOne={30}
            intervalOneText={"30 Days"}
            intervalTwo={90}
            intervalTwoText={"90 Days"}
            intervalThree={180}
            intervalThreeText={"180 Days"}
            intervalFour={365}
            intervalFourText={"Last Year"}
            onClickRefresh={getData}
            hideBtnId="shelter-refresh-button"
          />
          {intakeData && (
            <Row id="occupancy-line" className="mb-5 justify-content-center">
              <Col xs={10}>
                <ShelterOccupancyLineChart data={intakeData} startDate={startDate} endDate={endDate} useDatePicker={true} />
              </Col>
            </Row>
          )}
          {intakeData && (
            <Row className="mb-5">
              <Col xs={6} className="mb-3">
                <Row>
                  <Col xs={6} md={5} xl={4} className="mb-3">
                    <ShelterIntakeDayMetric
                      data={intakeData}
                      setSelectedData={setSelectedData}
                      startDate={startDate}
                      active={activeCard === "Intakes"}
                      useDatePicker={true}
                      onHandleClick={() => onClickCard("Intakes")}
                    />
                  </Col>
                  <Col xs={6} md={5} xl={4} className="mb-3">
                    <ShelterExitDayMetric
                      data={intakeData}
                      setSelectedData={setSelectedData}
                      active={activeCard === "Exits"}
                      startDate={startDate}
                      useDatePicker={true}
                      onHandleClick={() => onClickCard("Exits")}
                    />
                  </Col>
                </Row>
                <Col xs={12}>
                  {activeCard && selectedData && (
                    <DataTable
                      tableHeadings={intakeExitHeaders}
                      tableData={selectedData}
                      title={activeCard}
                      hideTitle={true}
                      overrideItemsPerPage={10}
                    />
                  )}
                </Col>
              </Col>
              <Col xs={6}>
                <ShelterExitTypePieChart data={exitData} startDate={startDate} endDate={endDate} useDatePicker={true} />
              </Col>
            </Row>
          )}
          <Row id="infractions-bar" className="mb-5 justify-content-center">
            <Col xs={10}>
              <InfractionsBarChart startDate={startDate} endDate={endDate} useDatePicker={true} />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default ShelterTab;
